class CustomerStoriesComponent extends HTMLElement {
  constructor() {
    super();
    this.onTabClick = this.onTabClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.init();
  }

  disconnectedCallback() {
    this.destroySwipers();
    this.unbindBreakpoint();
    window.clearTimeout(this.swiperWaitTimer);
  }

  init() {
    this.tabList = this.querySelector('[data-tab-list]');
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    this.sectionId = this.getAttribute('data-section-id');
    this.modal = document.getElementById(`CustomerStoriesModal-${this.sectionId}`);
    this.modalMedia = this.modal?.querySelector('[data-modal-media]');
    this.modalTitle = this.modal?.querySelector('[data-modal-title]');
    this.modalBody = this.modal?.querySelector('[data-modal-body]');
    this.activeFilter = this.activeFilter || '';
    this.swipers = this.swipers || new Map();

    this.bindBreakpoint();
    this.bindTabs();
    this.bindCards();
    this.setupVideos();
    this.setFilter(this.activeFilter);

    if (this.modal && this.modal.dataset.csBound !== 'true') {
      this.modal.dataset.csBound = 'true';
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.onModalClosed();
      });
      this.modal.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => {
        this.onModalClosed();
      });
      this.modal.addEventListener('keyup', (event) => {
        if (event.code === 'ESCAPE') this.onModalClosed();
      });
    }
  }

  bindBreakpoint() {
    this.unbindBreakpoint();
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.isMobile = this.mobileQuery.matches;
    this.onBreakpointChange = (event) => {
      this.isMobile = event.matches;
      window.clearTimeout(this.swiperWaitTimer);
      this.destroySwipers();
      window.requestAnimationFrame(() => {
        this.activatePanel(this.activePanel);
      });
    };

    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    } else {
      this.mobileQuery.addListener(this.onBreakpointChange);
    }
  }

  unbindBreakpoint() {
    if (!this.mobileQuery || !this.onBreakpointChange) return;

    if (this.mobileQuery.removeEventListener) {
      this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
    } else {
      this.mobileQuery.removeListener(this.onBreakpointChange);
    }

    this.onBreakpointChange = null;
  }

  bindTabs() {
    if (!this.tabList) return;
    this.tabList.removeEventListener('click', this.onTabClick);
    this.tabList.addEventListener('click', this.onTabClick);
  }

  onTabClick(event) {
    const tab = event.target.closest('[data-tab]');
    if (!tab || !this.tabList.contains(tab)) return;
    this.setFilter(tab.getAttribute('data-filter') || '');
  }

  setFilter(filter) {
    const tabs = this.tabList
      ? Array.from(this.tabList.querySelectorAll('[data-tab]'))
      : [];
    const selectedTab =
      tabs.find((tab) => (tab.getAttribute('data-filter') || '') === filter) || tabs[0];
    const nextFilter = selectedTab?.getAttribute('data-filter') || '';
    const previousPanel = this.activePanel;

    if (previousPanel) {
      this.pausePanel(previousPanel);
      this.pausePanelVideos(previousPanel);
    }

    this.activeFilter = nextFilter;

    tabs.forEach((tab) => {
      const active = tab === selectedTab;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      const active = (panel.getAttribute('data-filter') || '') === nextFilter;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) this.activePanel = panel;
    });

    if (!this.activePanel) return;

    window.requestAnimationFrame(() => {
      this.activatePanel(this.activePanel);
    });
  }

  autoplayEnabled() {
    return (
      this.getAttribute('data-autoplay') === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  autoplayDelay() {
    const delay = Number.parseInt(this.getAttribute('data-delay'), 10);
    return Number.isFinite(delay) && delay > 0 ? delay : 5000;
  }

  shouldAutoRotate(slideCount) {
    return this.isMobile ? slideCount > 1 : slideCount > 4;
  }

  activatePanel(panel) {
    if (!panel || panel.hidden) return;
    if (panel.hasAttribute('data-featured-static') && !this.isMobile) return;

    const existing = this.swipers.get(panel);
    if (existing) {
      existing.update();
      this.startPanel(panel);
      return;
    }

    this.createPanelSwiper(panel, 0);
  }

  createPanelSwiper(panel, attempt) {
    if (!panel || panel.hidden || !this.isConnected) return;

    if (!window.XBLOOM3?.Swiper) {
      if (attempt >= 20) return;
      window.clearTimeout(this.swiperWaitTimer);
      this.swiperWaitTimer = window.setTimeout(() => {
        this.createPanelSwiper(panel, attempt + 1);
      }, 100);
      return;
    }

    const container = panel.querySelector('[data-story-swiper]');
    if (!container || this.swipers.has(panel)) return;
    const slideCount = container.querySelectorAll('.swiper-slide').length;
    const featuredStatic = panel.hasAttribute('data-featured-static');
    const canAutoRotate = !featuredStatic && this.shouldAutoRotate(slideCount);

    const swiper = new window.XBLOOM3.Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: 20,
      speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 500,
      loop: canAutoRotate,
      loopedSlides: canAutoRotate ? slideCount : 0,
      loopAdditionalSlides: canAutoRotate ? 1 : 0,
      rewind: false,
      grabCursor: true,
      watchOverflow: !canAutoRotate,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      breakpoints: {
        750: {
          slidesOffsetBefore: 40,
          slidesOffsetAfter: 40,
        },
      },
      autoplay: canAutoRotate && this.autoplayEnabled()
        ? {
            delay: this.autoplayDelay(),
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    });

    swiper.customerStoriesCanAutoRotate = canAutoRotate;
    this.swipers.set(panel, swiper);
    this.startPanel(panel);
  }

  startPanel(panel) {
    if (
      !panel ||
      panel !== this.activePanel ||
      !this.autoplayEnabled() ||
      this.modal?.hasAttribute('open')
    ) {
      return;
    }

    const swiper = this.swipers.get(panel);
    if (swiper?.customerStoriesCanAutoRotate && swiper.autoplay && !swiper.isLocked) {
      swiper.autoplay.start();
    }
  }

  pausePanel(panel) {
    const swiper = this.swipers.get(panel);
    if (swiper?.autoplay) swiper.autoplay.stop();
  }

  pausePanelVideos(panel) {
    panel?.querySelectorAll('.customer-stories__video').forEach((video) => {
      this.pauseVideo(video);
    });
  }

  destroySwipers() {
    if (!this.swipers) return;
    this.swipers.forEach((swiper) => {
      try {
        swiper.destroy(true, true);
      } catch (error) {
        // Swiper may already be destroyed by the theme editor.
      }
    });
    this.swipers.clear();
  }

  bindCards() {
    if (this.onCardClick) this.removeEventListener('click', this.onCardClick);
    if (this.onCardKeydown) this.removeEventListener('keydown', this.onCardKeydown);
    if (this.onCardMouseover) this.removeEventListener('mouseover', this.onCardMouseover);
    if (this.onCardMouseout) this.removeEventListener('mouseout', this.onCardMouseout);

    this.onCardClick = (event) => {
      const card = event.target.closest('[data-card]');
      if (!card || !this.activePanel?.contains(card)) return;
      if (this.activePanel.hasAttribute('data-featured-static')) return;
      const swiper = this.swipers.get(this.activePanel);
      if (event.defaultPrevented || (swiper && !swiper.allowClick)) return;
      this.openModal(card);
    };

    this.onCardKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const card = event.target.closest('[data-card]');
      if (!card || !this.activePanel?.contains(card)) return;
      if (this.activePanel.hasAttribute('data-featured-static')) return;
      event.preventDefault();
      this.openModal(card);
    };

    this.onCardMouseover = (event) => {
      const card = event.target.closest('[data-card]');
      if (!card || !this.activePanel?.contains(card)) return;
      if (event.relatedTarget && card.contains(event.relatedTarget)) return;
      this.playVideo(card.querySelector('.customer-stories__video'));
    };

    this.onCardMouseout = (event) => {
      const card = event.target.closest('[data-card]');
      if (!card || !this.activePanel?.contains(card)) return;
      if (event.relatedTarget && card.contains(event.relatedTarget)) return;
      this.pauseVideo(card.querySelector('.customer-stories__video'));
    };

    this.addEventListener('click', this.onCardClick);
    this.addEventListener('keydown', this.onCardKeydown);
    this.addEventListener('mouseover', this.onCardMouseover);
    this.addEventListener('mouseout', this.onCardMouseout);
  }

  setupVideos() {
    this.querySelectorAll('.customer-stories__video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;
    });
  }

  playVideo(video) {
    if (!video || this.isMobile || !video.paused) return;
    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
  }

  pauseVideo(video) {
    if (!video || video.paused) return;
    video.pause();
  }

  parseStoryData(card) {
    const node = card.querySelector('[data-story-modal]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  openModal(card) {
    if (!this.modal || typeof this.modal.show !== 'function') return;

    const data = this.parseStoryData(card);
    if (!data) return;

    this.pausePanel(this.activePanel);

    if (this.modalMedia) {
      this.modalMedia.innerHTML = '';
      if (data.image) {
        const image = document.createElement('img');
        image.src = data.image;
        image.alt = data.imageAlt || data.title || '';
        this.modalMedia.appendChild(image);
      }
    }

    if (this.modalTitle) this.modalTitle.textContent = data.title || '';
    if (this.modalBody) this.modalBody.innerHTML = data.body || '';

    this.modal.show(card);
  }

  onModalClosed() {
    window.setTimeout(() => {
      this.startPanel(this.activePanel);
    }, 550);
  }
}

if (!customElements.get('customer-stories-component')) {
  customElements.define('customer-stories-component', CustomerStoriesComponent);
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const component = event.target.querySelector('customer-stories-component');
    if (!component || typeof component.init !== 'function') return;
    component.initialized = false;
    component.destroySwipers();
    if (component.modal) component.modal.dataset.csBound = '';
    component.init();
    component.initialized = true;
  });
}
